"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Zap, ArrowRight, CircleCheck as CheckCircle2, Award, Clock, Cpu } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type Language = 'es' | 'en' | 'eu' | 'fr';
const LANGUAGE_STORAGE_KEY = 'wvc_language';
const LANGUAGE_EVENT_NAME = 'wvc-language-change';
function useLanguage(): [Language, (lang: Language) => void] {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window === "undefined") return "es";
    try {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored === "es" || stored === "en" || stored === "eu" || stored === "fr") {
        return stored;
      }
    } catch {
      // ignore error
    }
    return "es";
  });
  useEffect(() => {
    const handleStorage = () => {
      try {
        const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (stored === "es" || stored === "en" || stored === "eu" || stored === "fr") {
          setLanguageState(stored);
        }
      } catch {}
    };
    const handleCustom = (e: Event) => {
      const detail = (e as CustomEvent<Language>).detail;
      if (detail === "es" || detail === "en" || detail === "eu" || detail === "fr") {
        setLanguageState(detail);
      }
    };
    window.addEventListener("storage", handleStorage);
    window.addEventListener(LANGUAGE_EVENT_NAME, handleCustom);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(LANGUAGE_EVENT_NAME, handleCustom);
    };
  }, []);
  const setLanguage = (lang: Language) => {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      window.dispatchEvent(new CustomEvent(LANGUAGE_EVENT_NAME, {
        detail: lang
      }));
    } catch {}
    setLanguageState(lang);
  };
  return [language, setLanguage];
}
const statConfigs = [{
  value: "+15",
  icon: Award
}, {
  value: "1.200+",
  icon: CheckCircle2
}, {
  value: "24/7",
  icon: Clock
}, {
  value: "Loxone",
  icon: Cpu
}];
const translations = {
  es: {
    eyebrow: "Nuestra especialidad · Partner Loxone",
    h1: "Instalaciones Eléctricas Integrales y Domótica Loxone de Alto Nivel.",
    highlight: "Domótica Loxone: tu casa, con inteligencia propia.",
    paragraph: "Transformamos viviendas, oficinas, institutos y teatros en espacios seguros e inteligentes en Bilbao y todo el País Vasco.",
    ctaQuote: "Solicitar Presupuesto",
    ctaCall: "Llamar ahora",
    trustLine: "Presupuestos transparentes, sin sorpresas ni costes ocultos.",
    statsLabels: ["Años de experiencia", "Proyectos completados", "Urgencias en todo el País Vasco", "Partner especializado"],
    imageBadgeTitle: "INSTALACIÓN CERTIFICADA",
    imageBadgeSubtitle: "Loxone Miniserver & Cuadro Eléctrico",
    tickerItems: ["DOMÓTICA LOXONE", "INSTALACIONES INDUSTRIALES Y RESIDENCIALES", "PUNTOS DE RECARGA VE", "BOLETINES Y LEGALIZACIONES", "URGENCIAS 24/7 EN BILBAO Y TODO EL PAÍS VASCO", "ILUMINACIÓN ARQUITECTÓNICA LED", "CUADROS ELÉCTRICOS DE PRECISIÓN"]
  },
  en: {
    eyebrow: "Our specialty · Loxone Partner",
    h1: "Comprehensive Electrical Installations and High-End Loxone Home Automation.",
    highlight: "Loxone Smart Home: your house, with a mind of its own.",
    paragraph: "We transform homes, offices, schools, and theaters into safe and smart spaces across Bilbao and the entire Basque Country.",
    ctaQuote: "Request a Quote",
    ctaCall: "Call now",
    trustLine: "Transparent quotes, no surprises or hidden fees.",
    statsLabels: ["Years of experience", "Completed projects", "24/7 Emergencies across Basque Country", "Specialized Partner"],
    imageBadgeTitle: "CERTIFIED INSTALLATION",
    imageBadgeSubtitle: "Loxone Miniserver & Electrical Panel",
    tickerItems: ["LOXONE SMART HOME", "INDUSTRIAL & RESIDENTIAL INSTALLATIONS", "EV CHARGING STATIONS", "CERTIFICATES & LEGALIZATIONS", "24/7 EMERGENCIES IN BILBAO & BASQUE COUNTRY", "ARCHITECTURAL LED LIGHTING", "PRECISION ELECTRICAL PANELS"]
  },
  eu: {
    eyebrow: "Gure espezialitatea · Loxone Partner",
    h1: "Instalazio Elektriko Integralak eta Goi Mailako Loxone Domotika.",
    highlight: "Loxone Domotika: zure etxea, berezko adimenarekin.",
    paragraph: "Etxebizitzak, bulegoak, ikastetxeak eta antzokiak gune seguru eta adimentsu bihurtzen ditugu Bilbon eta Euskal Herri osoan.",
    ctaQuote: "Aurrekontua eskatu",
    ctaCall: "Deitu orain",
    trustLine: "Aurrekontu gardenak, ezusterik eta ezkutuko gasturik gabe.",
    statsLabels: ["Esperientzia-urteak", "Bukatutako proiektuak", "Larrialdiak Euskal Herri osoan", "Partner espezializatua"],
    imageBadgeTitle: "ZIURTATUTAKO INSTALAZIOA",
    imageBadgeSubtitle: "Loxone Miniserver eta Koadro Elektrikoa",
    tickerItems: ["LOXONE DOMOTIKA", "INSTALAZIO INDUSTRIALAK ETA BIZITZEKOAK", "IB KARGA-GUNEAK", "BULETINAK ETA LEGEZTAPENAK", "24/7 LARRIALDIAK BILBON ETA EUSKAL HERRIA OSOAN", "ARKITEKTURA-ARGIZTAPEN LED", "PREZISIOZKO KOADRO ELEKTRIKOAK"]
  },
  fr: {
    eyebrow: "Notre spécialité · Partenaire Loxone",
    h1: "Installations Électriques Intégrales et Domotique Loxone Haut de Gamme.",
    highlight: "Domotique Loxone : votre maison, dotée d'une intelligence propre.",
    paragraph: "Nous transformons logements, bureaux, établissements scolaires et théâtres en espaces sûrs et intelligents à Bilbao et dans tout le Pays Basque.",
    ctaQuote: "Demander un devis",
    ctaCall: "Appeler maintenant",
    trustLine: "Devis transparents, sans surprises ni frais cachés.",
    statsLabels: ["Années d'expérience", "Projets réalisés", "Urgences dans tout le Pays Basque", "Partenaire spécialisé"],
    imageBadgeTitle: "INSTALLATION CERTIFIÉE",
    imageBadgeSubtitle: "Loxone Miniserver & Tableau Électrique",
    tickerItems: ["DOMOTIQUE LOXONE", "INSTALLATIONS INDUSTRIELLES ET RÉSIDENTIELLES", "BORNES DE RECHARGE VE", "CERTIFICATS ET LÉGALISATIONS", "URGENCES 24/7 À BILBAO ET DANS TOUT LE PAYS BASQUE", "ÉCLAIRAGE ARCHITECTURAL LED", "TABLEAUX ÉLECTRIQUES DE PRÉCISION"]
  }
};
export default function EngineeringHero() {
  const [language] = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.es;
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="ingeniería-eléctrica-domótica-loxone-de-alto-nivel-en-bilbao" data-nav="dark" className="relative min-h-screen w-full bg-[#0d1023] text-foreground overflow-hidden pt-24 pb-12 lg:pt-32 lg:pb-16">
      {/* Background Architectural Grid Pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-15 bg-[radial-gradient(ellipse_at_top,var(--color-primary)_0%,transparent_60%)]" aria-hidden="true" />
      <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(to_right,hsl(220,20%,18%)/0.15_1px,transparent_1px),linear-gradient(to_bottom,hsl(220,20%,18%)/0.15_1px,transparent_1px)] bg-[size:4rem_4rem]" aria-hidden="true" />

      <div className="mx-auto max-w-[1904px] px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left Column: Hero Text & Information */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            {/* Tag / Eyebrow */}
            <motion.div variants={itemVariants} className="inline-flex items-center">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#131a30]/80 border border-primary/30 px-3.5 py-1.5 text-xs font-mono text-primary font-semibold tracking-wider uppercase backdrop-blur-md">
                <span className="size-2 rounded-full bg-primary animate-pulse" />
                {t.eyebrow}
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground leading-[1.1] font-sans">
              {t.h1}
            </motion.h1>

            {/* Highlight Callout */}
            <motion.div variants={itemVariants} className="flex items-center gap-3 text-primary font-medium text-lg lg:text-xl">
              <Zap className="size-5 shrink-0" />
              <span>{t.highlight}</span>
            </motion.div>

            {/* Supporting Paragraph */}
            <motion.p variants={itemVariants} className="text-lg sm:text-xl text-muted-foreground max-w-2xl leading-relaxed font-sans">
              {t.paragraph}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-[#25D366] text-[#0a0e20] hover:bg-[#25D366]/90 font-bold px-7 py-6 text-base rounded-full transition-[color,background-color,box-shadow] duration-200">
                <Link to="https://wa.me/34662498828" newTab>
                  {t.ctaQuote}
                  <ArrowRight className="size-5 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="secondary" size="lg" className="bg-secondary text-secondary-foreground border border-white/15 hover:bg-white/10 font-semibold px-7 py-6 text-base rounded-full transition-[color,background-color,border-color] duration-200">
                <a href="tel:946424918">
                  {t.ctaCall}
                </a>
              </Button>
            </motion.div>

            {/* Transparency Trust Line */}
            <motion.div variants={itemVariants} className="flex items-center gap-2 text-sm text-muted-foreground pt-1">
              <ShieldCheck className="size-4 text-primary shrink-0" />
              <span>{t.trustLine}</span>
            </motion.div>

            {/* Metrics 4-Grid */}
            <motion.div variants={itemVariants} className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6 border-t border-white/10">
              {statConfigs.map((stat, i) => {
              const Icon = stat.icon;
              return <div key={i} data-index={i} className="p-4 rounded-xl bg-card border border-white/10 flex flex-col gap-1 hover:border-primary/40 transition-[border-color] duration-200">
                    <div className="flex items-center gap-2">
                      <Icon className="size-4 text-primary shrink-0" />
                      <span className="text-2xl sm:text-3xl font-bold font-mono text-foreground tracking-tight">
                        {stat.value}
                      </span>
                    </div>
                    <span className="text-xs sm:text-sm text-muted-foreground font-medium">
                      {t.statsLabels[i]}
                    </span>
                  </div>;
            })}
            </motion.div>
          </div>

          {/* Right Column: Technical Image Showcase */}
          <motion.div variants={itemVariants} className="lg:col-span-5 relative flex justify-center items-center">
            <div className="relative w-full max-w-xl rounded-2xl overflow-hidden border border-white/15 bg-card group">
              {/* Image Frame Decorator Bar */}
              <div className="flex items-center justify-between px-4 py-3 bg-[#0a0e20]/80 border-b border-white/10 text-xs font-mono text-muted-foreground">
                <div className="flex items-center gap-2">
                  <span className="size-2.5 rounded-full bg-destructive/80" />
                  <span className="size-2.5 rounded-full bg-chart-4/80" />
                  <span className="size-2.5 rounded-full bg-primary" />
                  <span className="ml-2 text-foreground/80 font-semibold">ELECTRO & MÁS · LOXONE SMART HUB</span>
                </div>
                <span className="text-primary text-[10px] tracking-wider uppercase font-bold">ONLINE 24/7</span>
              </div>

              {/* Main Content Image */}
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/photo-1663811397207-418a92396ad5_3441724fb1.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/photo-1663811397207-418a92396ad5_3441724fb1.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/photo-1663811397207-418a92396ad5_3441724fb1.jpg-wvc-sizes" alt="Instalación eléctrica realizada por técnicos electricistas expertos y automatización domótica Loxone en Bilbao, sus alrededores y en todo el País Vasco" loading="eager" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />

                {/* Subtle Image Vignette Scrim */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d1023] via-transparent to-transparent opacity-80" />

                {/* Floating Technical Status Badge on Image */}
                <div className="absolute bottom-4 left-4 right-4 p-3.5 rounded-xl bg-[#0a0e20]/90 backdrop-blur-md border border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="size-9 rounded-lg bg-primary/20 border border-primary/40 flex items-center justify-center text-primary">
                      <Cpu className="size-5" />
                    </div>
                    <div>
                      <div className="text-xs font-mono font-bold text-foreground">{t.imageBadgeTitle}</div>
                      <div className="text-[11px] text-muted-foreground">{t.imageBadgeSubtitle}</div>
                    </div>
                  </div>
                  <span className="text-xs font-mono text-primary font-semibold px-2.5 py-1 rounded-md bg-primary/10 border border-primary/30">
                    OK
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Technical Marquee Ticker */}
      <div className="mt-16 border-y border-white/10 bg-[#0a0e20]/60 py-3.5 overflow-hidden backdrop-blur-sm">
        <div className="flex whitespace-nowrap animate-marquee gap-8 items-center">
          {[...t.tickerItems, ...t.tickerItems].map((item, idx) => <div key={idx} data-index={idx} className="flex items-center gap-8 shrink-0">
              <span className="text-xs sm:text-sm font-mono font-semibold tracking-widest text-muted-foreground/90 uppercase">
                {item}
              </span>
              <span className="size-1.5 rounded-full bg-primary/60 shrink-0" />
            </div>)}
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          display: flex;
          width: max-content;
          animation: marquee 35s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>;
}